import React from "react";
import { Sequence, interpolate } from "remotion";
import { useAudioComponent } from "./VideoLayer.js";
import { audibleTracks, type AudioTrack } from "@veyso/core";
import { useAssetUrl } from "../assets.js";

/**
 * Builds a per-frame volume curve for a track's fades. Remotion accepts a
 * function here, which is how a fade stays sample-accurate at any fps.
 */
function volumeCurve(track: AudioTrack, fps: number, totalFrames: number) {
  const fadeInFrames = Math.round(track.fadeIn * fps);
  const fadeOutFrames = Math.round(track.fadeOut * fps);
  if (fadeInFrames <= 0 && fadeOutFrames <= 0) return track.volume;

  return (frame: number) => {
    const rising =
      fadeInFrames > 0
        ? interpolate(frame, [0, fadeInFrames], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })
        : 1;
    const falling =
      fadeOutFrames > 0
        ? interpolate(frame, [totalFrames - fadeOutFrames, totalFrames], [1, 0], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          })
        : 1;
    return track.volume * Math.min(rising, falling);
  };
}

/**
 * Where a track sits, in frames.
 *
 * Extracted so the contract can be asserted without rendering: `fps` must be
 * the rate the composition is being counted at, never the timeline's own. The
 * two differ every time the editor previews a 60fps project through a 30fps
 * proxy, and getting it wrong slides the audio away from the picture.
 */
export function audioTrackFrames(track: Pick<AudioTrack, "startAt" | "duration">, fps: number) {
  return {
    from: Math.round(track.startAt * fps),
    durationInFrames: Math.max(1, Math.round(track.duration * fps)),
  };
}

/**
 * Renders every audio track that has its own file, the recording's original
 * audio included — one code path for all sound, so a layer's behaviour does not
 * depend on where it came from.
 *
 * A muted track is skipped rather than played at zero: it still exists in the
 * timeline, and the editor still shows it, but it costs nothing to render.
 */
export const AudioTracks: React.FC<{ tracks: AudioTrack[]; fps: number }> = ({ tracks, fps }) => {
  const assetUrl = useAssetUrl();
  // Remotion's own <Audio> on the server, <Audio> from @remotion/media in a
  // browser render. Unset means the former, so the worker is unaffected.
  const AudioLayer = useAudioComponent();

  return (
    <>
      {audibleTracks(tracks).map((track) => {
        if (!track.assetKey) return null;

        const { from, durationInFrames } = audioTrackFrames(track, fps);

        return (
          <Sequence
            key={track.id}
            from={from}
            durationInFrames={durationInFrames}
            layout="none"
            name={`audio-${track.id}`}
          >
            <AudioLayer
              src={assetUrl(track.assetKey)!}
              volume={volumeCurve(track, fps, durationInFrames)}
              trimBefore={track.trimStart > 0 ? Math.round(track.trimStart * fps) : undefined}
              loop={track.loop}
            />
          </Sequence>
        );
      })}
    </>
  );
};
